// src/types/finance/FinanceTypes.ts
//
// ── Money on the wire (finance I2 onwards) ───────────────────────────────────────────────────
//
// The finance service returns EVERY money figure twice and the two are different JSON types:
//
//   `totalIncCents: 27100`   integer minor units — the SOURCE OF TRUTH, always an int
//   `total: "271.00"`        a **string**, because the dollar columns are Postgres `numeric`
//                            GENERATED columns and TypeORM does not coerce numeric to a number
//
// This file types them accordingly. `MoneyString` exists so a reader cannot mistake one of those
// fields for something arithmetic may be done on: render it with
// `utils/finance/money.ts::formatDollarString`, compute with the `*Cents` field.

/** A dollar-denominated amount as it arrives on the wire — e.g. `"271.00"`. Never a number. */
export type MoneyString = string;

export { TaxCode, PriceEntryMode } from "../../utils/finance/gst";
import type { TaxCode, PriceEntryMode } from "../../utils/finance/gst";
import type { UserUuid } from "../identity";

export enum PaymentType {
	SUBSCRIPTION = "subscription",
	BOOKING = "booking",
	ECOMMERCE = "ecommerce",
	INVOICE = "invoice",
}

export enum PaymentStatus {
	PENDING = "pending",
	PROCESSING = "processing",
	COMPLETED = "completed",
	FAILED = "failed",
	REFUNDED = "refunded",
	CANCELLED = "cancelled",
	/**
	 * Recorded in error and backed out (finance I5). Deliberately NOT `refunded` (no money moved)
	 * and NOT `cancelled` (the payment did complete). A reversed row is excluded from
	 * `SUM(completed)`, which is what makes the invoice's `amountPaidCents` fall back.
	 */
	REVERSED = "reversed",
}

/**
 * How money arrived. `stripe` is the processor path and is never recordable by hand — the manual
 * ledger endpoints accept and reverse everything EXCEPT `stripe`.
 */
export enum PaymentMethod {
	STRIPE = "stripe",
	CASH = "cash",
	EFT = "eft",
	BANK_TRANSFER = "bank_transfer",
	CHEQUE = "cheque",
	OTHER = "other",
}

/** The methods an operator may record or reverse by hand, in the order the select offers them. */
export const MANUAL_PAYMENT_METHODS: PaymentMethod[] = [
	PaymentMethod.CASH,
	PaymentMethod.EFT,
	PaymentMethod.BANK_TRANSFER,
	PaymentMethod.CHEQUE,
	PaymentMethod.OTHER,
];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
	[PaymentMethod.STRIPE]: "Stripe",
	[PaymentMethod.CASH]: "Cash",
	[PaymentMethod.EFT]: "EFT",
	[PaymentMethod.BANK_TRANSFER]: "Bank transfer",
	[PaymentMethod.CHEQUE]: "Cheque",
	[PaymentMethod.OTHER]: "Other",
};

export enum InvoiceStatus {
	DRAFT = "draft",
	SENT = "sent",
	/** Some money has arrived, the balance is not zero (finance I4/I5). */
	PARTIALLY_PAID = "partially_paid",
	PAID = "paid",
	/**
	 * ⚠️ **DERIVED, not stored, from finance I4 on.** Nothing writes it any more; lateness is
	 * computed server-side and surfaced as `Invoice.effectiveStatus`. Pre-I4 rows may still
	 * carry it as a stored `status`. Render `effectiveStatus`, never re-derive lateness here —
	 * the client's clock and the database's are not the same clock.
	 */
	OVERDUE = "overdue",
	CANCELLED = "cancelled",
}

/**
 * What the document may legally call itself. Decided server-side at issue: `tax_invoice` only
 * when the clinic has an ABN **and** is GST-registered.
 *
 * ⚠️ When this is `invoice`, the renderer must suppress the GST summary and must never print the
 * word "Tax". `null` on a draft — nothing has been issued, so nothing has been decided.
 */
export enum InvoiceDocumentType {
	TAX_INVOICE = "tax_invoice",
	INVOICE = "invoice",
}

/**
 * How a LOCALLY-collected (non-Stripe) invoice is to be paid — any subset of the clinic's bank/EFT
 * details, its own configured payment link (PAYLINK) and its PayID (PAYID). Per-invoice, chosen at
 * create as a MULTI-select, default `['bank_transfer']`.
 *
 * Only meaningful for a non-Stripe invoice: a Stripe-collected invoice uses its hosted page
 * regardless. Every selected+configured method renders together, non-exclusively. Mirrors the
 * backend `LocalPaymentMethod` enum (`finance/src/modules/invoices/local-payment-method.enum.ts`).
 */
export enum LocalPaymentMethod {
	BANK_TRANSFER = "bank_transfer",
	PAYMENT_LINK = "payment_link",
	PAYID = "payid",
}

/**
 * What kind of identifier a clinic's PayID is — mirrors the backend `PayIdType`
 * (`finance/src/modules/settings/payid-type.enum.ts`).
 */
export enum PayIdType {
	MOBILE = "mobile",
	EMAIL = "email",
}

export enum SubscriptionStatus {
	ACTIVE = "active",
	CANCELED = "canceled",
	CANCELLED = "cancelled",
	INCOMPLETE = "incomplete",
	INCOMPLETE_EXPIRED = "incomplete_expired",
	PAST_DUE = "past_due",
	TRIALING = "trialing",
	UNPAID = "unpaid",
}

export enum TransactionType {
	PAYMENT = "payment",
	REFUND = "refund",
	CHARGEBACK = "chargeback",
}

export interface Payment {
	id: string;
	userId: string;
	type: PaymentType;
	/** Integer minor units — the source of truth. */
	amountCents?: number;
	/** ⚠️ A dollar STRING (`"25.99"`), not a number. See the header of this file. */
	amount: MoneyString;
	currency: string;
	status: PaymentStatus;
	/** How the money arrived. Absent on rows written before finance I5, which were all Stripe. */
	method?: PaymentMethod;
	/** Cheque number, EFT reference, receipt book number — the operator's external handle. */
	reference?: string | null;
	/** The date the money ARRIVED (`YYYY-MM-DD`), not the date it was typed in. */
	receivedAt?: string | null;
	/** Set when this payment settles an invoice; the ledger is keyed on it. */
	paymentMethodId?: string;
	metadata?: {
		stripe_payment_intent_id?: string;
		stripe_client_secret?: string;
		stripe_customer_id?: string;
		stripe_charge_id?: string;
		[key: string]: any;
	};
	invoiceId?: string;
	subscriptionId?: string;
	bookingId?: string;
	description?: string;
	failureReason?: string;
	createdAt: string;
	updatedAt: string;
}

/**
 * Where an invoice line CAME FROM (S8) — the frontend mirror of finance's
 * `InvoiceLineSourceType`. Provenance, not a price: the practitioner's typed price is what bills,
 * and the catalogue's own figure is snapshotted beside it server-side so a discount reads as a
 * variance rather than erasing the fact that a catalogue item was sold.
 *
 * ⚠️ **Internal only (parent decision A4).** None of it reaches the patient-facing document —
 * `PublicInvoiceLineDto` is a hand-built allow-list and a SKU or a variant id has no place on an
 * invoice a patient reads. Do not add any of these fields to `PublicInvoiceTypes`.
 */
export enum InvoiceLineSourceType {
	/** Free text typed by the practitioner — the default, and what every pre-S8 line reads as. */
	MANUAL = "manual",
	/** Picked from the commerce catalogue. The lines an issued invoice moves stock for. */
	COMMERCE_PRODUCT = "commerce_product",
	/** Picked from the healthcare appointment types. A service, so it never touches inventory. */
	APPOINTMENT_TYPE = "appointment_type",
}

export interface InvoiceItem {
	id: string;
	invoiceId?: string;
	description: string;
	quantity: number;
	sortOrder?: number;

	/** The tax treatment of this line. */
	taxCode: TaxCode;
	/**
	 * The rate that WAS applied, in basis points, SNAPSHOTTED when the line was created. Never
	 * re-derive it from `taxCode` for display — a rate change must not re-rate an issued invoice.
	 */
	taxRateBps: number;
	/** False marks a line whose tax figures did NOT come from the GST engine (an import). */
	taxComputed?: boolean;

	// ── Money: integers ──────────────────────────────────────────────────────────────────────
	unitPriceCents: number;
	lineExCents: number;
	lineTaxCents: number;
	lineIncCents: number;

	// ── Money: legacy dollar strings ─────────────────────────────────────────────────────────
	/** `"110.00"`. ⚠️ String. */
	unitPrice: MoneyString;
	/**
	 * `"100.00"`. ⚠️ String, and it mirrors **lineExCents**, not the inclusive figure — the
	 * legacy invariant is `invoice.subtotal === Σ item.amount` and `subtotal` is ex-GST. Under
	 * inclusive entry that makes `amount !== quantity * unitPrice`.
	 */
	amount: MoneyString;

	// ── Provenance (S8) ──────────────────────────────────────────────────────────────────────
	// Every key is OPTIONAL here and not on the wire: finance always sends them, but a line read
	// from a pre-S8 deployment (or from a test fixture) carries none, and an absent `sourceType`
	// means exactly what `manual` means. Read them through `draftFromItem`, never raw.
	sourceType?: InvoiceLineSourceType;
	sourceProductId?: number | null;
	sourceVariantId?: number | null;
	sourceAppointmentTypeId?: number | null;
	sourceSku?: string | null;
	/** The catalogue's own unit price at pick time, integer cents. SERVER-DERIVED — see S9. */
	catalogueUnitPriceCents?: number | null;
	/**
	 * The line came from a catalogue AND is not being charged at the catalogue price. DERIVED
	 * server-side from the two figures above; false for a manual line and for a line at list price.
	 */
	priceOverridden?: boolean;
}

export interface Invoice {
	id: string;
	/**
	 * ⚠️ **NULL until the invoice is ISSUED.** A draft is not a document and does not consume a
	 * number. Render an unnumbered draft as `Draft · <first 8 of id>` — never as an empty cell,
	 * which reads as a data-loading bug.
	 */
	invoiceNumber: string | null;
	/** The user's uuid; null only on a pre-cutover row whose legacy id had no mapping. */
	userId: string;
	/** Legacy: finance's old userUuid column is frozen and no longer mapped, so the API never sends it; read userId. */
	userUuid?: string | null;

	// ── Money: integers, the source of truth ─────────────────────────────────────────────────
	subtotalExCents: number;
	taxCents: number;
	totalIncCents: number;
	amountPaidCents: number;
	/** `totalIncCents - amountPaidCents`. Derived server-side; never negative-clamped. */
	balanceDueCents: number;

	// ── Money: legacy dollar strings ─────────────────────────────────────────────────────────
	/** ⚠️ Strings (`"271.00"`), on the create response as well as on reads. */
	subtotal: MoneyString;
	tax: MoneyString;
	total: MoneyString;

	/** The STORED status. `overdue` here means a legacy pre-I4 row, not "late". */
	status: InvoiceStatus;
	/**
	 * **The status to RENDER.** The stored status, except that a late invoice reads `overdue`.
	 * Computed server-side against the database's clock — do not re-derive it in the browser.
	 */
	effectiveStatus: InvoiceStatus;

	priceEntryMode: PriceEntryMode;

	issueDate: string;
	dueDate: string;
	paidDate?: string | null;
	/** When the number was allocated. NULL ⇔ still a draft. */
	issuedAt?: string | null;
	paymentId?: string | null;
	notes?: string | null;

	/**
	 * How a locally-collected invoice is to be paid — a SET of any of `bank_transfer`, `payment_link`
	 * (PAYLINK) and `payid` (PAYID). Defaults to `['bank_transfer']`; only meaningful when
	 * `collectViaStripe` is false. Optional here because pre-PAYLINK rows predate the column (the
	 * server defaults them to `['bank_transfer']`).
	 */
	localPaymentMethods?: LocalPaymentMethod[];

	// ── Stripe collection ────────────────────────────────────────────────────────────────────
	/** Chosen at create, acted on at ISSUE. Nothing exists in Stripe before the invoice is issued. */
	collectViaStripe: boolean;
	stripeCustomerId?: string | null;
	stripeInvoiceId?: string | null;
	/** The hosted payment page. Present once the mirror has been built and finalized. */
	stripeHostedInvoiceUrl?: string | null;
	/** "Issued locally, Stripe mirror not built yet" — the state behind `stripe_mirror_pending`. */
	stripeSyncPending?: boolean;

	uncollectibleAt?: string | null;
	lastPaymentFailureAt?: string | null;

	/** Non-null ⇒ imported from another system ⇒ IMMUTABLE here (every mutation is a 409). */
	source?: string | null;
	externalId?: string | null;

	// ── Issued-document snapshot, frozen at issue. All null on a draft. ──────────────────────
	documentType?: InvoiceDocumentType | null;
	clinicAbn?: string | null;
	clinicLegalName?: string | null;
	/** Pre-formatted: `<street>, <suburb> <state> <postcode>`, empty parts dropped. */
	clinicAddress?: string | null;
	/** Pre-formatted: `<email> | <phone>`, empty parts dropped. */
	clinicContact?: string | null;

	items: InvoiceItem[];
	metadata?: {
		stripe_invoice_id?: string;
		stripe_customer_id?: string;
		/**
		 * ⚠️ **The DURABLE record of a stock shortfall (S10) — read THIS, not the warning, for
		 * anything shown after the issue moment.** `ISSUE_WARNING_STOCK_SHORTFALL` rides only the
		 * response of the request that actually delivered the fulfilment; commerce answers an
		 * idempotent replay with `shortfalls: []` and cannot reconstruct them, so on a lost
		 * response the warning is gone and this is the only place the fact survives.
		 */
		stock_shortfalls?: StockShortfall[];
		/** ISO timestamp of the delivery that recorded `stock_shortfalls`. */
		stock_shortfall_at?: string;
		[key: string]: any;
	};
	createdAt: string;
	updatedAt: string;
}

/**
 * One row of an invoice's payment ledger (`GET /invoices/:id/payments`), newest `receivedAt`
 * first. A reversed row is kept and marked, never deleted.
 */
export interface InvoicePayment {
	id: string;
	invoiceId?: string;
	userId?: string;
	amountCents: number;
	/** ⚠️ String. */
	amount: MoneyString;
	currency?: string;
	status: PaymentStatus;
	method: PaymentMethod;
	reference?: string | null;
	receivedAt?: string | null;
	createdAt: string;
	updatedAt?: string;
}

export interface Subscription {
	id: string;
	userId: string;
	customerId: string;
	priceId: string;
	status: SubscriptionStatus;
	amount?: number;
	currency: string;
	currentPeriodStart?: string;
	currentPeriodEnd?: string;
	canceledAt?: string;
	cancelAtPeriodEnd: boolean;
	metadata?: {
		stripe_subscription_id?: string;
		stripe_customer_id?: string;
		stripe_price_id?: string;
		[key: string]: any;
	};
	createdAt: string;
	updatedAt: string;
}

export interface Transaction {
	id: string;
	paymentId: string;
	userId: string;
	type: TransactionType;
	amount: number;
	currency: string;
	transactionId?: string;
	metadata?: any;
	createdAt: string;
}

export interface CreatePaymentRequest {
	userId: string;
	type: PaymentType;
	amount: number;
	currency?: string;
	paymentMethodId: string;
	description?: string;
	metadata?: any;
	invoiceId?: string;
	subscriptionId?: string;
	bookingId?: string;
}

/**
 * One line on the way IN. Cents only — the deprecated dollar `unitPrice` alias still exists
 * server-side for pre-I2 callers and is deliberately not offered here.
 */
export interface CreateInvoiceItemRequest {
	description: string;
	quantity: number;
	unitPriceCents: number;
	taxCode: TaxCode;
	sortOrder?: number;

	// ── Provenance (S8) ──────────────────────────────────────────────────────────────────────
	// ⚠️ `forbidNonWhitelisted` is ON server-side, so these are sendable ONLY because
	// `InvoiceItemDto` declares them — an undeclared key is a 400, not an ignore. `null` is
	// accepted for each (`@IsOptional()` skips both null and undefined), which is how a line
	// switched back to Manual clears the ids it used to carry.
	sourceType?: InvoiceLineSourceType;
	sourceProductId?: number | null;
	sourceVariantId?: number | null;
	sourceAppointmentTypeId?: number | null;
	sourceSku?: string | null;
	/**
	 * ⚠️ **Deliberately NOT sent by this app.** The DTO accepts it, but S9 OVERWRITES it with the
	 * price finance itself read from the catalogue — including with `null` when the catalogue was
	 * unreachable. A client-supplied figure is worthless as the audit value behind
	 * `priceOverridden`, which exists to show the discount against what the catalogue really
	 * charged. Declared here only so nobody re-adds it thinking it was an oversight.
	 */
	catalogueUnitPriceCents?: number | null;
}

/**
 * The ONE create body — `POST /invoices`.
 *
 * ⚠️ `POST /invoices/stripe` is DEPRECATED and returns `stripeInvoice: null`; it is not used
 * anywhere in this app any more. Set `collectViaStripe` here instead — the Stripe mirror is
 * built at ISSUE, from the frozen lines, never at create.
 *
 * `issueDate`, `dueDate`, `paymentTermsDays` and `priceEntryMode` are all optional; the server
 * fills them from `clinic_settings`. Sending `undefined` is how you take the clinic default.
 */
export interface CreateInvoiceRequest {
	userId: string;
	userUuid?: string;
	/** The practitioner's user uuid; transient, it picks whose rate prices appointment-type lines and must be the uuid the picker quoted with. */
	practitionerId?: UserUuid;
	collectViaStripe?: boolean;
	stripeCustomerId?: string;
	currency?: string;
	priceEntryMode?: PriceEntryMode;
	/** Days from `issueDate` to `dueDate`. Ignored when `dueDate` is supplied. 0..365. */
	paymentTermsDays?: number;
	items: CreateInvoiceItemRequest[];
	issueDate?: string;
	dueDate?: string;
	notes?: string;
	metadata?: Record<string, any>;
	/**
	 * How to collect a non-Stripe invoice (PAYLINK/PAYID) — a SET of any of bank/EFT, the clinic's
	 * payment link and its PayID, at least one. Defaults to `['bank_transfer']` server-side when
	 * omitted; omit it for a Stripe-collected invoice, which is paid through its hosted page
	 * regardless.
	 */
	localPaymentMethods?: LocalPaymentMethod[];
}

/**
 * `PUT /invoices/:id` — **drafts only**. A non-draft, paid, cancelled or imported invoice is a
 * 409. `items`, when present, REPLACES the line set wholesale.
 *
 * ⚠️ Changing `priceEntryMode` alone on a legacy flat-tax invoice is a 400
 * ("Re-supply line items to change price entry mode…") — send the lines with it.
 */
export interface UpdateInvoiceRequest {
	items?: CreateInvoiceItemRequest[];
	/**
	 * See `CreateInvoiceRequest.practitionerId`. Re-sent on every edit precisely because `items`
	 * replaces the line set wholesale — the server deletes and re-inserts every row, so a draft
	 * edit re-resolves the snapshot from scratch rather than carrying the first resolution forward.
	 */
	practitionerId?: UserUuid;
	priceEntryMode?: PriceEntryMode;
	issueDate?: string;
	dueDate?: string;
	paymentTermsDays?: number;
	collectViaStripe?: boolean;
	stripeCustomerId?: string;
	currency?: string;
	notes?: string;
}

/** `POST /invoices/:id/payments` — money that arrived out of band, typed in by a human. */
export interface RecordInvoicePaymentRequest {
	/** Integer minor units, strictly positive. Part payments are allowed. */
	amountCents: number;
	/** Anything except `stripe` — a Stripe payment is created by Stripe. */
	method: PaymentMethod;
	reference?: string;
	/** `YYYY-MM-DD`. The date the money ARRIVED. Defaults to today server-side. */
	receivedAt?: string;
}

/** Server-side list controls for `GET /invoices/clinic`. */
export interface InvoiceListParams {
	page?: number;
	per_page?: number;
	status?: InvoiceStatus;
	/** ⚠️ Matches `invoiceNumber` ONLY — so a DRAFT can never appear in a search result. */
	search?: string;
	/** Inclusive lower bound on `issueDate` (`YYYY-MM-DD`). */
	from?: string;
	/** Inclusive upper bound on `issueDate` (`YYYY-MM-DD`). */
	to?: string;
	userId?: string;
	sort?: InvoiceSortField;
	order?: "asc" | "desc";
}

/** The columns the list endpoint will order by. An allow-list server-side; anything else 400s. */
export type InvoiceSortField =
	| "invoiceNumber"
	| "issueDate"
	| "dueDate"
	| "paidDate"
	| "total"
	| "status"
	| "createdAt";

/**
 * The clinic's own identity and invoicing preferences — one singleton row,
 * `GET`/`PUT /clinic-settings`. `null` when the clinic has never saved them (NOT a 404).
 */
export interface ClinicSettings {
	id: string;
	legalName: string;
	tradingName?: string | null;
	/** 11 digits, stored bare. With `gstRegistered`, decides "Tax Invoice" vs "Invoice". */
	abn?: string | null;
	gstRegistered: boolean;

	street?: string | null;
	suburb?: string | null;
	state?: string | null;
	postcode?: string | null;
	contactEmail?: string | null;
	contactPhone?: string | null;

	invoiceNumberPrefix: string;
	invoiceNumberPadding: number;

	defaultPaymentTermsDays: number;
	defaultTaxCode: TaxCode;
	defaultPriceEntryMode: PriceEntryMode;
	payLinkTtlDays: number;

	invoiceFooterText?: string | null;
	bankAccountName?: string | null;
	/** 6 digits, stored bare. */
	bankBsb?: string | null;
	bankAccountNumber?: string | null;
	/**
	 * The clinic's OWN invoice payment link (PAYLINK) — ONE clinic-wide URL, offered per-invoice as
	 * an alternative to the bank details. Read live, never snapshotted. `null` when unconfigured.
	 */
	invoicePaymentLink?: string | null;
	/**
	 * The clinic's PayID (PAYID) — a `mobile` or `email` kind (`payIdType`) plus the identifier value
	 * (`payId`). ONE clinic-wide pair, offered per-invoice like the bank details and the link. Read
	 * live, never snapshotted. `null` when unconfigured.
	 */
	payIdType?: string | null;
	payId?: string | null;

	createdAt?: string;
	updatedAt?: string;
}

/**
 * The settings fields that are NULLABLE columns and can therefore be CLEARED.
 *
 * ⚠️ Everything not on this list (`legalName`, `gstRegistered`, the numbering and defaults) is a
 * NOT NULL column — sending `null` for one of those is a 500, not a clear.
 */
export const CLEARABLE_CLINIC_SETTINGS_FIELDS = [
	"tradingName",
	"abn",
	"street",
	"suburb",
	"state",
	"postcode",
	"contactEmail",
	"contactPhone",
	"invoiceFooterText",
	"bankAccountName",
	"bankBsb",
	"bankAccountNumber",
	"invoicePaymentLink",
	"payIdType",
	"payId",
] as const;

export type ClearableClinicSettingsField = (typeof CLEARABLE_CLINIC_SETTINGS_FIELDS)[number];

/**
 * `PUT /clinic-settings`. **Merge semantics** — only the keys present are written. `legalName`
 * is the one required field. ABN and BSB accept the separators people type; the server strips
 * them before validating, so `12 345 678 901` and `12345678901` are the same request.
 *
 * ── Clearing a previously-set field: send `null`, never `""` ─────────────────────────────────
 *
 * The nullable fields are typed `string | null` because the three states are genuinely distinct
 * and the server treats them differently:
 *
 *   `undefined`  omit the key — leave the stored value alone (TypeORM's `merge` skips undefined)
 *   `null`       CLEAR it — `@IsOptional()` short-circuits every other validator for null, so it
 *                reaches `merge` untouched and is written as NULL
 *   `""`         a 400 for `abn` (`@Matches(/^\d{11}$/)`), `bankBsb` (`/^\d{6}$/`) and
 *                `contactEmail` (`@IsEmail()`); for the rest it STORES an empty string rather
 *                than clearing, so the column is then `''` and not NULL
 *
 * `""` is therefore wrong in both directions — it fails on three fields and half-works on the
 * others. `null` is uniform.
 */
export interface UpdateClinicSettingsRequest {
	legalName: string;
	tradingName?: string | null;
	abn?: string | null;
	gstRegistered?: boolean;
	street?: string | null;
	suburb?: string | null;
	state?: string | null;
	postcode?: string | null;
	contactEmail?: string | null;
	contactPhone?: string | null;
	invoiceNumberPrefix?: string;
	invoiceNumberPadding?: number;
	defaultPaymentTermsDays?: number;
	defaultTaxCode?: TaxCode;
	defaultPriceEntryMode?: PriceEntryMode;
	payLinkTtlDays?: number;
	invoiceFooterText?: string | null;
	bankAccountName?: string | null;
	bankBsb?: string | null;
	bankAccountNumber?: string | null;
	/** The clinic's own invoice payment link (PAYLINK). `null` clears it, `undefined` leaves it. */
	invoicePaymentLink?: string | null;
	/** The clinic's PayID kind (PAYID) — `mobile`/`email`. `null` clears it, `undefined` leaves it. */
	payIdType?: string | null;
	/** The clinic's PayID value (PAYID). `null` clears it, `undefined` leaves it. */
	payId?: string | null;
}

export interface RefundRequest {
	amount?: number;
	reason?: string;
}

// ==================== CUSTOMER TYPES ====================

export interface StripeAddress {
	line1?: string;
	line2?: string;
	city?: string;
	state?: string;
	postal_code?: string;
	country?: string;
}

export interface StripeCustomer {
	id: string;
	object: "customer";
	address?: StripeAddress | null;
	balance: number;
	created: number;
	currency?: string | null;
	customer_account?: string | null;
	default_source?: string | null;
	delinquent: boolean;
	description?: string | null;
	discount?: any | null;
	email?: string | null;
	invoice_prefix: string;
	invoice_settings: {
		custom_fields?: any | null;
		default_payment_method?: string | null;
		footer?: string | null;
		rendering_options?: any | null;
	};
	livemode: boolean;
	metadata: Record<string, any>;
	name?: string | null;
	next_invoice_sequence: number;
	phone?: string | null;
	preferred_locales: string[];
	shipping?: any | null;
	tax_exempt: string;
	test_clock?: any | null;
}

export interface CreateFinanceCustomerRequest {
	userId: string;
	email: string;
	name?: string;
	phone?: string;
	address?: StripeAddress;
	metadata?: Record<string, any>;
}

export interface UpdateFinanceCustomerRequest {
	email?: string;
	name?: string;
	phone?: string;
	address?: StripeAddress;
	metadata?: Record<string, any>;
}

// ==================== PAYMENT METHOD TYPES ====================

export interface StripePaymentMethod {
	id: string;
	type: string;
	card?: {
		brand: string;
		last4: string;
		exp_month: number;
		exp_year: number;
		funding: string;
	};
	billing_details?: {
		name?: string;
		email?: string;
		phone?: string;
		address?: StripeAddress;
	};
	created: number;
}

export interface AttachPaymentMethodRequest {
	paymentMethodId: string;
	customerId: string;
}

export interface SetDefaultPaymentMethodRequest {
	paymentMethodId: string;
	customerId: string;
}

// ==================== PAYMENT INTENT TYPES ====================

export interface CreateStripePaymentIntentRequest {
	userId: string;
	amount: number;
	currency?: string;
	customerId?: string;
	paymentMethodId?: string;
	description?: string;
	invoiceId?: string;
	subscriptionId?: string;
	bookingId?: string;
	metadata?: Record<string, any>;
	confirm?: boolean;
}

export interface StripePaymentIntentResponse {
	paymentId: string;
	paymentIntentId: string;
	clientSecret: string;
	status: string;
	amount: number;
	currency: string;
}

export interface ConfirmPaymentIntentRequest {
	paymentIntentId: string;
	paymentMethodId?: string;
}

export interface CancelPaymentIntentRequest {
	paymentIntentId: string;
	cancellationReason?: string;
}

// ==================== SUBSCRIPTION TYPES ====================

export interface CreateSubscriptionRequest {
	userId: string;
	customerId: string;
	priceId: string;
	paymentMethodId?: string;
	trialPeriodDays?: number;
	metadata?: Record<string, any>;
}

export interface UpdateSubscriptionRequest {
	priceId?: string;
	paymentMethodId?: string;
	cancelAtPeriodEnd?: boolean;
	metadata?: Record<string, any>;
}

export interface CancelSubscriptionRequest {
	cancelAtPeriodEnd?: boolean;
}

export interface StripeSubscription {
	id: string;
	customer: string;
	status: string;
	current_period_start: number;
	current_period_end: number;
	cancel_at_period_end: boolean;
	canceled_at?: number;
	trial_start?: number;
	trial_end?: number;
	items: {
		data: Array<{
			id: string;
			price: {
				id: string;
				unit_amount: number;
				currency: string;
				recurring?: {
					interval: string;
					interval_count: number;
				};
			};
		}>;
	};
	latest_invoice?: any;
	metadata?: Record<string, any>;
}

export interface SubscriptionResponse {
	subscription: Subscription;
	stripeSubscription: StripeSubscription;
}

// ==================== INVOICE TYPES ====================
//
// ⚠️ `CreateStripeInvoiceRequest` / `FinalizeInvoiceRequest` / `PayInvoiceRequest` /
// `VoidInvoiceRequest` were REMOVED in I8 along with the endpoints that consumed them.
//
// Those flows were broken two ways and could not have worked:
//   1. the page gated them on `invoice.metadata.stripe_invoice_id`, which the create path stopped
//      writing when the Stripe mirror moved to issue time — so the buttons never rendered; and
//   2. every one of those request bodies carried `companyId`, which has been a 400 from the
//      global `forbidNonWhitelisted` ValidationPipe since the company removal.
//
// The replacement is `POST /invoices/:id/issue` plus the manual payment ledger. Do not
// reintroduce them: `POST /invoices/stripe` is deprecated and returns `stripeInvoice: null`.

export interface StripeInvoice {
	id: string;
	customer: string;
	amount_due: number;
	amount_paid: number;
	amount_remaining: number;
	currency: string;
	status: string;
	hosted_invoice_url?: string;
	invoice_pdf?: string;
	due_date?: number;
	paid?: boolean;
	created: number;
	metadata?: Record<string, any>;
}

/**
 * What issuing returns. `warnings` is EMPTY on the happy path.
 *
 * ⚠️ **`stripe_mirror_pending` is not an error.** The invoice IS issued — numbered, snapshotted,
 * immutable — and only its Stripe payment page is missing, because the local issue has to commit
 * before the Stripe round trip. Surface it as "Issued — payment link pending" with a retry
 * affordance (re-issuing re-claims and rebuilds), never as a failure.
 */
export interface IssueInvoiceResult {
	invoice: Invoice;
	stripeInvoice: StripeInvoice | null;
	warnings: string[];
}

/** Emitted by `issue` when the local invoice is numbered but its Stripe mirror is not built. */
export const ISSUE_WARNING_STRIPE_MIRROR_PENDING = "stripe_mirror_pending";

/**
 * Emitted by `issue`/`send` when commerce could not fully supply one or more product lines (S10).
 *
 * ⚠️ **A WARNING, never a refusal** (parent decision D2). Commerce consumes what it has, takes the
 * remainder NEGATIVE, and the invoice is issued, numbered and payable either way — clinical billing
 * is never blocked by a stocktake discrepancy. Nothing in the UI may gate, disable or confirm on
 * it; it is information about the shelf, delivered after the fact.
 *
 * ⚠️ **It rides only the response of the request that actually delivered the fulfilment.** A
 * replayed delivery is answered `shortfalls: []` by commerce, which cannot reconstruct the list, so
 * this warning is the sub-second signal and `metadata.stock_shortfalls` is the durable record.
 * Anything displayed after the issue moment reads the metadata.
 */
export const ISSUE_WARNING_STOCK_SHORTFALL = "stock_shortfall";

/**
 * One line commerce could not fully supply, exactly as finance persists it onto
 * `invoice.metadata.stock_shortfalls`.
 *
 * snake_case because it is commerce's own shape, carried through finance verbatim. The ids are
 * commerce's integer primary keys — the same values an invoice line carries as `sourceProductId` /
 * `sourceVariantId`, which is how a shortfall is matched back to the line that caused it (finance
 * holds no product names).
 *
 * ⚠️ `available` is what was on the shelf BEFORE the consumption, so the resulting level is
 * `available - requested` — negative, deliberately.
 */
export interface StockShortfall {
	product_id: number;
	variant_id: number;
	requested: number;
	available: number;
}

/**
 * What recording or reversing a manual payment returns. `warnings` carries the same meaning as
 * on `IssueInvoiceResult` — the money IS recorded; something in Stripe needs a human.
 */
export interface PaymentLedgerResult {
	invoice: Invoice;
	payment: InvoicePayment;
	warnings: string[];
}

/** Stripe's copy of this invoice now disagrees with ours and only a human can fix it. */
export const PAYMENT_WARNING_STRIPE_SETTLEMENT_PENDING = "stripe_settlement_pending";

// ==================== DASHBOARD TYPES ====================

export interface CompanyDashboardStats {
	overview: {
		payments: {
			total: number;
			completed: number;
			pending: number;
			failed: number;
			refunded: number;
			totalAmount: number;
			completedAmount: number;
			refundedAmount: number;
		};
		invoices: {
			total: number;
			draft: number;
			sent: number;
			paid: number;
			overdue: number;
			cancelled: number;
			totalAmount: number;
			paidAmount: number;
			unpaidAmount: number;
		};
	};
	monthlyRevenue: Array<{
		month: string;
		amount: number;
	}>;
	paymentsByType: Array<{
		type: string;
		count: number;
		amount: number;
	}>;
	recentActivity?: {
		payments: Payment[];
		invoices: Invoice[];
		transactions: Transaction[];
	};
}

export interface UserDashboardStats {
	payments: {
		total: number;
		completed: number;
		pending: number;
		failed: number;
		totalAmount: number;
	};
	invoices: {
		total: number;
		paid: number;
		pending: number;
		overdue: number;
		totalAmount: number;
	};
	recentActivity?: {
		payments: Payment[];
		invoices: Invoice[];
		transactions: Transaction[];
	};
}

// ==================== API RESPONSE TYPES ====================

export interface ApiResponse<T> {
	success: boolean;
	data: T;
	message?: string;
	/**
	 * ⚠️ **TOP LEVEL, beside `success`/`data` — not inside `data`**, and OMITTED entirely on the
	 * happy path so the ordinary shape is unchanged. A response carrying warnings SUCCEEDED.
	 */
	warnings?: string[];
}

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page?: number;
	perPage?: number;
}

/**
 * The list envelope exactly as `GET /invoices/clinic` sends it — snake_case `per_page`, and
 * `total`/`page` beside `data` rather than in a `pagination` object.
 */
export interface InvoiceListEnvelope {
	success: boolean;
	data: Invoice[];
	total: number;
	page: number;
	per_page: number;
}

// ==================== DEFAULT VALUES ====================

export const defaultPayment: Partial<Payment> = {
	type: PaymentType.BOOKING,
	amount: "0.00",
	amountCents: 0,
	currency: "AUD",
	status: PaymentStatus.PENDING,
	description: "",
};

export const defaultInvoice: Partial<Invoice> = {
	invoiceNumber: null,
	subtotalExCents: 0,
	taxCents: 0,
	totalIncCents: 0,
	amountPaidCents: 0,
	balanceDueCents: 0,
	subtotal: "0.00",
	tax: "0.00",
	total: "0.00",
	status: InvoiceStatus.DRAFT,
	effectiveStatus: InvoiceStatus.DRAFT,
	collectViaStripe: false,
	items: [],
	notes: "",
};

export const defaultSubscription: Partial<Subscription> = {
	status: SubscriptionStatus.INCOMPLETE,
	currency: "AUD",
	cancelAtPeriodEnd: false,
};

export const defaultStripeCustomer: Partial<CreateFinanceCustomerRequest> = {
	email: "",
	name: "",
	phone: "",
	address: {
		country: "AU",
	},
};

export const defaultStripePaymentIntent: Partial<CreateStripePaymentIntentRequest> = {
	amount: 0,
	currency: "AUD",
	confirm: false,
};

export const defaultCreateSubscription: Partial<CreateSubscriptionRequest> = {
	trialPeriodDays: 0,
};

/**
 * ⚠️ `defaultCreateStripeInvoice` was removed with `CreateStripeInvoiceRequest` — see the note in
 * the INVOICE TYPES section. There is one create body now, and a Stripe-collected invoice is
 * `CreateInvoiceRequest` with `collectViaStripe: true`.
 */
export const defaultCreateInvoice: Partial<CreateInvoiceRequest> = {
	collectViaStripe: false,
	items: [],
};